import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { ArrowUpRight, Code, Brain, Cpu, Zap } from 'lucide-react';

export function Projects() {
  const { theme } = useTheme();

  const projectCategories = [
    {
      name: "Huxley",
      description: "A model-agnostic framework for long-horizon biological AI systems. Foundational infrastructure for computational biology and AI-driven research.",
      path: "/projects/huxley",
      icon: Cpu,
      category: "Computational Biology",
      status: "Active",
      features: [
        "Multi-provider LLM abstraction",
        "Agent orchestration & planning",
        "Protein structure generation",
        "PDB integration & chemistry tools"
      ]
    }
  ];

  return (
    <div className="relative">
      <Background3D />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-semibold mb-8 leading-tight">
              Our <span className="text-gradient">Projects</span>
            </h1>
            <p className={`text-xl leading-relaxed max-w-3xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Huxley represents our commitment to advancing computational biology through innovative, 
              open-source infrastructure designed for the next generation of biological AI systems.
            </p>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-4 gap-8 mb-20">
            {[
              { number: "1", label: "Active Project", icon: Code },
              { number: "5+", label: "LLM Providers", icon: Brain },
              { number: "20+", label: "Built-in Tools", icon: Cpu },
              { number: "MIT", label: "License", icon: ArrowUpRight }
            ].map((stat, index) => (
              <div
                key={index}
                className={`text-center p-6 rounded-2xl ${
                  theme === 'dark' 
                    ? 'bg-dark-200/50 border border-dark-300' 
                    : 'bg-white/50 border border-gray-200'
                } backdrop-blur-sm`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                }`}>
                  <stat.icon className="w-6 h-6 text-primary-500" />
                </div>
                <div className="text-3xl font-semibold mb-2">{stat.number}</div>
                <div className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-12 text-center">
            Featured Projects
          </h2>
          
          <div className="grid gap-8">
            {projectCategories.map((project, index) => (
              <Link
                key={index}
                to={project.path}
                className={`group p-8 rounded-2xl border transition-all duration-500 hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-dark-200/50 border-dark-300 hover:border-primary-500'
                    : 'bg-white/50 border-gray-200 hover:border-primary-300'
                } backdrop-blur-sm`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-xl ${
                      theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                    }`}>
                      <project.icon className="w-6 h-6 text-primary-500" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold mb-2">{project.name}</h3>
                      <div className="flex items-center space-x-4 mb-3">
                        <span className={`px-3 py-1 rounded-full text-sm ${
                          project.status === 'Active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {project.status}
                        </span>
                        <span className={`text-sm ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {project.category}
                        </span>
                      </div>
                    </div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-primary-500 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>

                <p className={`mb-6 leading-relaxed ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {project.description}
                </p>

                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Key Features</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {project.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${
                          theme === 'dark' ? 'bg-primary-400' : 'bg-primary-500'
                        }`} />
                        <span className={`text-sm ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-semibold mb-4">
              Innovation Through Collaboration
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Our projects represent the intersection of cutting-edge research, practical application, 
              and human-centered design. We build solutions that accelerate human potential.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Modular Architecture",
                description: "Extensible components for seamless integration with any LLM provider or biological tool.",
                icon: Code
              },
              {
                title: "Research-Driven", 
                description: "Built on rigorous computational biology methodology and AI research best practices.",
                icon: Brain
              },
              {
                title: "Open Source",
                description: "MIT licensed framework fostering collaboration and innovation in biological AI research.",
                icon: Cpu
              }
            ].map((item, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl ${
                  theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
                } backdrop-blur-sm`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                  theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                }`}>
                  <item.icon className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="text-lg font-semibold mb-3">{item.title}</h3>
                <p className={`text-sm leading-relaxed ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <h2 className="text-4xl font-semibold mb-8">
            Interested in <span className="text-gradient">collaborating</span>?
          </h2>
          <p className={`text-xl leading-relaxed mb-8 max-w-2xl mx-auto ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            We're always looking for partnerships, research collaborations, 
            and opportunities to advance human potential through technology.
          </p>
          <Link
            to="/company"
            className="group inline-flex items-center px-8 py-4 text-lg text-white bg-primary-600 rounded-full hover:bg-primary-700 transition-all duration-300 shadow-soft hover:shadow-lg"
          >
            Learn More About Us
            <ArrowUpRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
